import axios, { AxiosInstance, AxiosError } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:55563/api';

class ApiService {
  private api: AxiosInstance;
  private isRefreshing = false;
  private refreshSubscribers: ((success: boolean) => void)[] = [];

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Envia cookies automaticamente
    });

    // Interceptor para refresh automático do token
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        // Se o erro for 401 e não for a rota de refresh, login ou me
        if (error.response?.status === 401 && !originalRequest._retry) {
          // Não tenta refresh em rotas de autenticação ou verificação inicial
          if (originalRequest.url?.includes('/auth/refresh') || 
              originalRequest.url?.includes('/auth/login') ||
              originalRequest.url?.includes('/auth/me')) {
            // Apenas rejeita o erro sem redirecionar
            return Promise.reject(error);
          }

          if (this.isRefreshing) {
            // Esperar o refresh em andamento
            return new Promise((resolve, reject) => {
              this.refreshSubscribers.push((success: boolean) => {
                if (success) {
                  resolve(this.api(originalRequest));
                } else {
                  reject(error);
                }
              });
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            // Tentar renovar o token
            await this.refresh();
            this.isRefreshing = false;
            this.onRefreshSuccess();
            return this.api(originalRequest);
          } catch (refreshError) {
            this.isRefreshing = false;
            this.onRefreshFailure();
            // Não redireciona automaticamente, deixa o AuthContext lidar
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private onRefreshSuccess() {
    this.refreshSubscribers.forEach(callback => callback(true));
    this.refreshSubscribers = [];
  }

  private onRefreshFailure() {
    this.refreshSubscribers.forEach(callback => callback(false));
    this.refreshSubscribers = [];
  }

  // Auth endpoints
  async register(username: string, password: string) {
    const response = await this.api.post('/auth/register', { username, password });
    return response.data;
  }

  async login(username: string, password: string) {
    const response = await this.api.post('/auth/login', { username, password });
    return response.data;
  }

  async logout() {
    const response = await this.api.post('/auth/logout');
    return response.data;
  }

  async refresh() {
    const response = await this.api.post('/auth/refresh');
    return response.data;
  }

  async me() {
    const response = await this.api.get('/auth/me');
    return response.data;
  }

  async getAllUsers() {
    const response = await this.api.get('/auth/users');
    return response.data;
  }

  async updateUser(username: string, data: { newUsername?: string; newPassword?: string }) {
    const response = await this.api.put(`/auth/users/${username}`, data);
    return response.data;
  }

  async deleteUser(username: string) {
    const response = await this.api.delete(`/auth/users/${username}`);
    return response.data;
  }

  // Lines endpoints
  async getAllLines() {
    const response = await this.api.get('/lines');
    return response.data;
  }

  async getLineByNumero(numero: string) {
    const response = await this.api.get(`/lines/${numero}`);
    return response.data;
  }

  async createLine(lineData: any) {
    const response = await this.api.post('/lines', lineData);
    return response.data;
  }

  async saveLine(lineData: any) {
    const response = await this.api.post('/lines', lineData);
    return response.data;
  }

  async saveLinesBulk(lines: any[]) {
    const response = await this.api.post('/lines/bulk', lines);
    return response.data;
  }

  async updateLine(numero: string, lineData: any) {
    const response = await this.api.put(`/lines/${numero}`, lineData);
    return response.data;
  }

  async deleteLine(numero: string) {
    const response = await this.api.delete(`/lines/${numero}`);
    return response.data;
  }

  async unlinkLine(numero: string) {
    const response = await this.api.post(`/lines/${numero}/unlink`);
    return response.data;
  }

  async getLinesByStatus(status: string) {
    const response = await this.api.get(`/lines/status/${status}`);
    return response.data;
  }

  // Get paginated lines with optional search and filter
  async getPaginatedLines(page: number = 1, limit: number = 20, search: string = '', status: string = 'all') {
    const response = await this.api.get('/lines/paginated', {
      params: { page, limit, search, status },
    });
    return response.data;
  }

  // Status endpoints
  async getLineStatus(numero: string) {
    const response = await this.api.get(`/status/${numero}`);
    return response.data;
  }

  async updateLineStatus(numero: string, liberado?: boolean, bloqueado?: boolean) {
    const body: any = {};
    if (liberado !== undefined) body.liberado = liberado;
    if (bloqueado !== undefined) body.bloqueado = bloqueado;
    const response = await this.api.post(`/status/${numero}`, body);
    return response.data;
  }

  // SGP endpoints
  async fetchClientData(cpfcnpj: string) {
    const response = await this.api.post('/sgp/cliente', { cpfcnpj });
    return response.data;
  }

  async fetchInvoices(cpfcnpj: string, contrato: string, senha?: string) {
    const response = await this.api.post('/sgp/faturas', { cpfcnpj, contrato, senha });
    return response.data;
  }
}

const apiService = new ApiService();
export default apiService;
